import { css } from '@emotion/react';
import { MEDIA_QUERIES, SPACER } from 'radiance-ui/lib/constants';

import { LOCAL_COLORS } from 'styles/constants';

import type { ColorType, CtaButtonSize } from '.';

const fitContent = 'fit-content';

export interface ButtonContainerStylesOptions {
  color: ColorType;
  /** Sanitized HEX color. Only applied when `color` is "custom". */
  customColor?: string;
  desktopSize: CtaButtonSize;
  mobileSize: CtaButtonSize;
  small: boolean;
  /** Sanitized HEX color for the button label. */
  textColor?: string;
}

const variantStyles = (color: ColorType, customColor?: string) => {
  switch (color) {
    // Note: this branch used to contain a stray `π` character, which stylis
    // parsed as part of the selector (`π .css-x button:hover`), so the hover
    // override below never actually applied. Removing it restores the intended
    // dark label on hover for every white CTA.
    case 'white':
      return `
        background-color: ${LOCAL_COLORS.surfaceWhite};
        border-color: ${LOCAL_COLORS.surfaceWhite};
        color: ${LOCAL_COLORS.textDefault};

        &:hover {
          color: ${LOCAL_COLORS.textDefault} !important;
        }
      `;
    case 'dark-blue':
      return `
        background-color: ${LOCAL_COLORS.surfaceDarkBold};
        border-color: ${LOCAL_COLORS.surfaceDarkBold};
        color: ${LOCAL_COLORS.surfaceLight};
      `;
    case 'custom':
      // Without a valid custom color we intentionally fall back to the primary styles.
      return customColor
        ? `
        background-color: ${customColor};
        border-color: ${customColor};
      `
        : '';
    default:
      return '';
  }
};

// Radiance's Button re-applies its own font color on hover/focus with a more
// specific selector, so the override needs !important (same as the "white" variant).
const textColorStyles = (textColor?: string) =>
  textColor
    ? `
      color: ${textColor};
      fill: ${textColor};

      &:hover,
      &:focus {
        color: ${textColor} !important;
        fill: ${textColor} !important;
      }
    `
    : '';

export const buttonContainerStyles = ({
  color,
  customColor,
  desktopSize,
  mobileSize,
  small,
  textColor,
}: ButtonContainerStylesOptions) => css`
  display: grid;
  /**
   * This is the disclaimer color, not the button label. White and dark-blue CTAs
   * are only used on dark sections, so their disclaimer is inverted. A custom
   * color says nothing about the section behind it, so the disclaimer keeps
   * inheriting from the module rather than guessing.
   */
  color: ${(color === 'white' || color === 'dark-blue') &&
  LOCAL_COLORS.textInverse};
  grid-template-rows: 3.5rem auto;
  margin: 0 auto;
  max-width: ${mobileSize === 'Small' || small ? '20.3125rem' : null};
  row-gap: ${SPACER.base};
  text-align: center;
  white-space: pre-line;
  width: ${small ? fitContent : '100%'};

  ${MEDIA_QUERIES.mdUp} {
    margin: 0;
    text-align: left;
    width: ${desktopSize === 'Small' || small ? fitContent : '17rem'};
  }

  button {
    background-color: ${LOCAL_COLORS.surfaceBrandBold};
    border-color: ${LOCAL_COLORS.surfaceBrandBold};
    border-radius: ${SPACER.x3large};
    font-size: 0.875rem;
    font-weight: bold;
    letter-spacing: unset;
    text-transform: unset;
    width: ${mobileSize === 'Small'
      ? fitContent
      : mobileSize === 'Large'
      ? '100%'
      : null};

    ${variantStyles(color, customColor)}

    ${textColorStyles(textColor)}

    ${MEDIA_QUERIES.mdUp} {
      width: ${desktopSize === 'Small'
        ? fitContent
        : desktopSize === 'Large'
        ? '100%'
        : null};
    }
  }
`;
